"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import AuthHeader from "../AuthHeader";
import SiteFooter from "../SiteFooter";
import { useLabsApiAuth } from "@/lib/labsIdentityContext";
import { formatExperimentStatus } from "@/lib/experimentFormatters";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

type Experiment = {
  id?: string;
  Id?: string;
  number?: string;
  Number?: string;
  title?: string;
  Title?: string;
  group?: string;
  Group?: string;
  description?: string;
  Description?: string;
  status?: string;
  Status?: string;
  enrollmentType?: string;
  EnrollmentType?: string;
  hasFullAccess?: boolean;
  accessState?: string;
};

function accessStateLabel(state: string | undefined): string {
  switch (state) {
    case "full": return "Full access";
    case "join_required": return "Join to view details";
    case "request_required": return "Request access";
    case "request_pending": return "Request pending";
    case "request_denied": return "Request pending";
    default: return "";
  }
}

export default function ExperimentsPage() {
  const { labsUserId: userId, headers: apiHeaders, loading: identityLoading } = useLabsApiAuth();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!API_URL || identityLoading) return;
    if (!userId) {
      if (!API_URL) setError("API URL not configured");
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    fetch(`${API_URL.replace(/\/$/, "")}/api/experiments`, {
      headers: apiHeaders,
    })
      .then((res) => {
        if (res.status === 401) {
          setError("Sign in to view experiments");
          return [];
        }
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? (data as Experiment[]) : [];
        setExperiments(list);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load experiments"))
      .finally(() => setLoading(false));
  }, [API_URL, userId, apiHeaders, identityLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8 dark:bg-gray-950">
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      </div>
    );
  }

  if (!userId && !loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
        <AuthHeader />
        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Experiments</h2>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Sign in to view experiments.</p>
          <a
            href={"/.auth/login/aad?post_login_redirect_uri=" + encodeURIComponent("/?fromLogin=1")}
            className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Sign in
          </a>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
        <AuthHeader />
        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          {error === "Sign in to view experiments" ? (
            <a href={"/.auth/login/aad?post_login_redirect_uri=" + encodeURIComponent("/?fromLogin=1")} className="mt-4 inline-block text-blue-600 hover:underline dark:text-blue-400">Sign in</a>
          ) : (
            <Link href="/" className="mt-4 inline-block text-blue-600 hover:underline dark:text-blue-400">Back to home</Link>
          )}
        </main>
      </div>
    );
  }

  const byGroup = (() => {
    const map = new Map<string, Experiment[]>();
    for (const exp of experiments) {
      const g = (exp.group ?? exp.Group ?? "").trim() || "\uFFFF"; // sort empty last
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(exp);
    }
    const groups = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return groups.map(([groupKey, list]) => ({
      groupLabel: groupKey === "\uFFFF" ? null : groupKey,
      experiments: list.sort((a, b) => (a.number ?? a.Number ?? "").localeCompare(b.number ?? b.Number ?? "", undefined, { sensitivity: "base" })),
    }));
  })();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      <AuthHeader />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Experiments</h2>
        {experiments.length === 0 ? (
          <p className="mt-4 text-gray-600 dark:text-gray-400">No experiments yet.</p>
        ) : (
          <div className="mt-6 space-y-8">
            {byGroup.map(({ groupLabel, experiments: list }) => (
              <section key={groupLabel ?? "other"}>
                {groupLabel && (
                  <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-200">{groupLabel}</h3>
                )}
                <ul className="space-y-4">
                  {list.map((exp) => {
                    const id = exp.id ?? exp.Id ?? "";
                    const number = exp.number ?? exp.Number ?? "";
                    const title = exp.title ?? exp.Title ?? "";
                    const description = exp.description ?? exp.Description ?? "";
                    const state = exp.accessState ?? "";
                    const label = accessStateLabel(state);
                    const statusRaw = exp.status ?? exp.Status ?? "";
                    const statusLabel = statusRaw ? formatExperimentStatus(statusRaw) : "";
                    return (
                      <li
                        key={id}
                        className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
                      >
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              {number && (
                                <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                                  {number}
                                </span>
                              )}
                              {label && (
                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                  state === "full" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" :
                                  state === "request_pending" || state === "request_denied" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" :
                                  "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200"
                                }`}>
                                  {label}
                                </span>
                              )}
                              {statusLabel && (
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                  {statusLabel}
                                </span>
                              )}
                            </div>
                            <Link
                              href={id ? `/experiments/${id}/` : "#"}
                              className="shrink-0 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                            >
                              View details →
                            </Link>
                          </div>
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                            {title || "Untitled experiment"}
                          </h3>
                          {description && (
                            <p className="line-clamp-3 text-sm text-gray-600 dark:text-gray-300">
                              {description}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
